import type { ICellRendererParams } from 'ag-grid-enterprise';
import { compact, isEmpty } from 'lodash-es';
import type { Security } from '../../types';
import { Box, HStack, Stack } from '../Core';
import { InlineFormattedNumber, NumberVariants } from '../FormattedNumber';
import { SecurityRenderer } from '../SecurityRenderer';
import { Sparkline } from './Sparkline';

const MIN_COLUMN_WIDTH_FOR_PERCENT_CHANGE = 190;

export function SecurityCellRenderer({ data, ...params }: ICellRendererParams) {
  const security: Security = data.security;

  const sparklineData = data?.sparklineData?.Values ?? [];
  const percentChange = data?.sparklineData?.PercentChange;
  const hasSparklineData = !isEmpty(compact(sparklineData));

  const variant = percentChange?.gt(0)
    ? NumberVariants.Positive
    : percentChange?.lt(0)
      ? NumberVariants.Negative
      : undefined;

  let shouldRenderPercentChange = !!percentChange;
  if (percentChange !== undefined) {
    const columnWidth = params.column?.getActualWidth();
    shouldRenderPercentChange = columnWidth ? columnWidth > MIN_COLUMN_WIDTH_FOR_PERCENT_CHANGE : false;
  }

  return (
    <HStack w="100%" h="100%" position="relative">
      <Box flex="1 1 0" overflow="hidden">
        <SecurityRenderer
          security={security}
          currency={security.BaseCurrency}
          showDescription={false}
          showIcon={true}
          iconSize={24}
          justifyContent="flex-start"
        />
      </Box>
      {shouldRenderPercentChange && (
        <Stack
          px="spacing4"
          fontSize="fontSizeSmall"
          position="relative"
          h="100%"
          justifyContent={hasSparklineData ? 'flex-start' : 'center'}
          mx="spacing4"
        >
          <Box top={hasSparklineData ? '20%' : '0'} position="relative">
            <InlineFormattedNumber
              showSign={true}
              number={percentChange}
              variant={variant}
              round={true}
              increment="0.01"
              currency="%"
            />
          </Box>
          {hasSparklineData && (
            <Box h="50%" left={0} right={0} top="40%" bottom="10%" position="absolute">
              <Sparkline
                h="100%"
                sparklineFill="gradient"
                data={sparklineData}
                variant={
                  percentChange?.gt(0)
                    ? NumberVariants.Positive
                    : percentChange?.lt(0)
                      ? NumberVariants.Negative
                      : undefined
                }
              />
            </Box>
          )}
        </Stack>
      )}
    </HStack>
  );
}
